import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface SourceData {
  source: string;
  cost: number;
  conversions: number;
  quotes: number;
  sales: number;
}

interface SourceBreakdownChartProps {
  data: SourceData[];
  metric: "cost" | "conversions" | "quotes" | "sales";
  title: string;
}

const COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
];

export function SourceBreakdownChart({
  data,
  metric,
  title,
}: SourceBreakdownChartProps) {
  const chartData = data.map((item) => ({
    name: item.source,
    value: item[metric],
  }));

  const formatValue = (value: number) => {
    if (metric === "cost") {
      return `£${value.toLocaleString()}`;
    }
    return value.toLocaleString();
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[250px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={2}
                dataKey="value"
              >
                {chartData.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                    className="stroke-card stroke-2"
                  />
                ))}
              </Pie>
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div className="bg-popover border border-popover-border rounded-md px-3 py-2 shadow-md">
                        <p className="text-sm font-medium">
                          {payload[0].name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {formatValue(payload[0].value as number)}
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend
                verticalAlign="bottom"
                height={36}
                formatter={(value) => (
                  <span className="text-xs text-foreground">{value}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
